import { useMemo } from 'react';

import dayjs from 'dayjs';
import useSWR from 'swr';

// Utils
import { get } from '@/utils/client';

// Lib
import { SCHEDULER_EVENTS } from '@/routes/apiEndpoints';

function generateDateRange(startDate, endDate) {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const dates = [];

    let currentDate = start;
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
        dates.push(currentDate.format('YYYY-MM-DD'));
        currentDate = currentDate.add(1, 'day');
    }

    return dates;
}

function groupByDateRange(data) {
    const grouped = {};

    data.forEach((item) => {
        item?.dateRange?.forEach((date) => {
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(item);
        });
    });

    return grouped;
}

const useSocialMediaEvents = ({ options = {} } = {}) => {
    const {
        data,
        mutate,
        error: socialMediaEventsError
    } = useSWR(
        SCHEDULER_EVENTS,
        (url) =>
            get(url).then((res) => {
                const events = res.data.events;
                return events.map((event) => {
                    const startDate = dayjs(event.start);
                    const endDate = dayjs(event.end);
                    return {
                        ...event,
                        end:
                            endDate > startDate
                                ? dayjs(event.end).add(1, 'day').format('YYYY-MM-DD')
                                : event.end,
                        startDate: event.start,
                        endDate: event.end,
                        dateRange: generateDateRange(event.start, event.end)
                    };
                });
            }),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    // return the events grouped by date
    const eventsPerDay = useMemo(() => {
        if (!data) return;

        return groupByDateRange(data);
    }, [data]);

    return {
        socialMediaEvents: data,
        eventsPerDay,
        mutateSocialMediaEvents: mutate,
        socialMediaEventsError,
        isLoadingSocialMediaEvents: !data && !socialMediaEventsError
    };
};

export default useSocialMediaEvents;
